import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VerifiedIcon from "@mui/icons-material/Verified";
import { Box, Chip, IconButton, TableCell, TableRow, TextField, Typography, useMediaQuery } from "@mui/material";
import { colors } from "../../../../../theme/muiColors";
import { useState } from "react";

import { colors as ink } from "../../../../ui/colors";
import { SalesResponse } from "../../../hooks/useGetSales";
import { TypeOfService } from "../../../types/scope-of-work.interface";
import { ResettableInput } from "../../ResettableInput";
import { roundMoney } from "./calculateTotal";
import type { InvoiceMainLineSeed } from "./computeInvoiceMainLineSeed";
import { formatCurrency } from "./helpers";

export interface ServicePrincipalRowProps {
  typeOfService: TypeOfService;
  serviceNameOverride?: string | null;
  serviceDescriptionOverride?: string | null;
  squareFootage?: string;
  salesData?: SalesResponse;
  isExpanded: boolean;
  onToggleExpand: () => void;
  allowEdit: boolean;
  editedValues: {
    servicePrice?: number | null;
    time?: number | null;
    serviceTotal?: number | null;
    isServiceLineTotalOverridden?: boolean;
  };
  onValueChange: (updates: {
    servicePrice?: number | null;
    serviceTotal?: number | null;
    time?: number | null;
    isServiceLineTotalOverridden?: boolean;
  }) => void;
  invoiceMainLineSeed?: InvoiceMainLineSeed | null;
  visit?: {
    uid: string;
    title: string;
    subtitle: string;
  };
  visitIndex?: number;
}

const DEFAULT_BACKEND_MINUTES = 60;

const sxCellOrange = {
  bgcolor: colors.orange[100],
};

function readOnlyMainLineSnapshot(sales: SalesResponse | undefined): {
  backendMinutes: number;
  baseHourlyRate: number;
} {
  if (!sales?.mainAreas) {
    return {
      backendMinutes: DEFAULT_BACKEND_MINUTES,
      baseHourlyRate: 0,
    };
  }

  const backendMinutes = Math.max(0, sales.mainAreas.time?.minutes ?? DEFAULT_BACKEND_MINUTES);

  const backendTotal = sales.mainAreas.cost ?? 0;

  const baseHourlyRate = backendMinutes > 0 ? roundMoney(backendTotal / (backendMinutes / 60)) : 0;

  return {
    backendMinutes,
    baseHourlyRate,
  };
}

export const ServicePrincipalRow = ({
  typeOfService,
  squareFootage,
  salesData,
  isExpanded,
  onToggleExpand,
  allowEdit,
  editedValues,
  onValueChange,
  invoiceMainLineSeed,
  visit,
  visitIndex,
  serviceNameOverride,
  serviceDescriptionOverride,
}: ServicePrincipalRowProps) => {
  const isMobile = useMediaQuery("(max-width: 800px)");

  const { backendMinutes, baseHourlyRate } = allowEdit
    ? {
        backendMinutes: 0,
        baseHourlyRate: 0,
      }
    : readOnlyMainLineSnapshot(salesData);

  const originalServiceName = typeOfService.name?.trim() || "Service";

  const serviceAlias = serviceNameOverride?.trim() || null;

  const displayServiceName = serviceAlias || originalServiceName;

  const customServiceDescription = serviceDescriptionOverride?.trim() || null;

  const originalServiceDescription = !serviceAlias && !customServiceDescription ? typeOfService.description?.trim() || null : null;

  const displayServiceDescription = customServiceDescription || originalServiceDescription;

  const shouldShowServiceHeader = !visit || visitIndex === 0;

  const time = allowEdit ? (editedValues.time ?? 0) : (editedValues.time ?? backendMinutes);

  const rate = allowEdit ? (editedValues.servicePrice ?? 0) : editedValues.servicePrice != null ? editedValues.servicePrice : baseHourlyRate;

  const qtyHours = time / 60;
  const computedLineTotal = roundMoney(qtyHours * rate);

  const displayLineTotal =
    editedValues.isServiceLineTotalOverridden === true && editedValues.serviceTotal != null ? editedValues.serviceTotal : computedLineTotal;

  const [qtyDraft, setQtyDraft] = useState<string | null>(null);

  const commitQtyFromString = (raw: string) => {
    const trimmed = raw.trim();

    if (trimmed === "" || trimmed === ".") {
      onValueChange({ time: 0 });
      return;
    }

    const parsed = Number.parseFloat(trimmed);

    if (Number.isNaN(parsed) || parsed < 0) {
      onValueChange({ time: 0 });
      return;
    }

    const minutes = Math.round(parsed * 60);

    onValueChange({
      time: Math.max(0, minutes),
    });
  };

  const handleQtyFocus = () => {
    if (!allowEdit) return;

    setQtyDraft(String(roundMoney(time / 60)));
  };

  const handleQtyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setQtyDraft(value);
    }
  };

  const handleQtyBlur = () => {
    const current = qtyDraft;

    setQtyDraft(null);

    const basis = current !== null ? current : String(roundMoney(time / 60));

    commitQtyFromString(basis);
  };

  const handleUnitPriceChange = (next: number | null) => {
    if (next == null) return;

    onValueChange({
      servicePrice: roundMoney(next),
    });
  };

  const defaultUnitPrice = invoiceMainLineSeed?.servicePrice ?? 0;

  const handleUnitPriceReset = () => {
    if (!invoiceMainLineSeed) return;

    onValueChange({
      servicePrice: invoiceMainLineSeed.servicePrice,
      time: invoiceMainLineSeed.timeMinutes,
      serviceTotal: invoiceMainLineSeed.gross,
      isServiceLineTotalOverridden: true,
    });
  };

  const alignmentSpacer =
    allowEdit && invoiceMainLineSeed && !isMobile ? (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 1,
          px: 0.5,
          visibility: "hidden",
          pointerEvents: "none",
        }}
      >
        <Box
          component="div"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            background: "none",
            border: "none",
            padding: 0,
            marginLeft: "auto",
            mb: -1,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.625rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Reset
          </Typography>

          <Box sx={{ width: 14, height: 14 }} />
        </Box>
      </Box>
    ) : null;

  const qtyField = allowEdit ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {alignmentSpacer}

      <TextField
        type="text"
        size="small"
        value={qtyDraft !== null ? qtyDraft : String(roundMoney(qtyHours))}
        onFocus={handleQtyFocus}
        onChange={handleQtyChange}
        onBlur={handleQtyBlur}
        disabled={!allowEdit}
        inputProps={{
          inputMode: "decimal",
          "aria-label": "Quantity in hours",
          style: {
            textAlign: "center",
            fontSize: "0.875rem",
            fontWeight: 500,
            color: colors.orange[700],
          },
        }}
        sx={{
          width: 96,

          "& .MuiOutlinedInput-root": {
            bgcolor: "grey.50",
            borderRadius: "0.5rem",
            height: "40px",

            "& fieldset": {
              borderColor: "grey.300",
              borderWidth: "1px",
            },

            "&:hover fieldset": {
              borderColor: ink.orange[400],
            },

            "&.Mui-focused fieldset": {
              borderColor: ink.orange[500],
              borderWidth: "2px",
            },

            "&.Mui-disabled": {
              bgcolor: "grey.100",

              "& fieldset": {
                borderColor: "grey.200",
              },
            },
          },

          "& .MuiOutlinedInput-input": {
            padding: "10px 10px",
          },
        }}
      />
    </Box>
  ) : (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        color: colors.orange[700],
        textAlign: "center",
        minWidth: 96,
      }}
    >
      {String(roundMoney(qtyHours))}
    </Typography>
  );

  const unitPriceInput = (
    <ResettableInput
      defaultValue={defaultUnitPrice}
      value={rate}
      onChange={allowEdit ? handleUnitPriceChange : undefined}
      onReset={allowEdit && invoiceMainLineSeed ? handleUnitPriceReset : undefined}
      readOnly={!allowEdit}
      formatValue={formatCurrency}
      width={isMobile ? "85px" : "100px"}
      fontSize="0.875rem"
      fontWeight={600}
      color={colors.orange[700]}
      showResetButton={Boolean(allowEdit && invoiceMainLineSeed)}
      resetTooltip="Restore original unit price, quantity, and line total"
      emptyCommitValue={defaultUnitPrice}
      align="right"
    />
  );

  const lineTotalCell = allowEdit ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      {alignmentSpacer}

      <Box
        sx={{
          height: "40px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <ResettableInput
          defaultValue={computedLineTotal}
          value={displayLineTotal}
          readOnly
          emptyCommitValue={0}
          formatValue={formatCurrency}
          width="120px"
          fontSize="0.875rem"
          fontWeight={editedValues.isServiceLineTotalOverridden ? 600 : 500}
          color={colors.orange[700]}
          showResetButton={false}
          align="right"
        />
      </Box>

      {editedValues.isServiceLineTotalOverridden ? (
        <Typography
          variant="caption"
          sx={{
            fontStyle: "italic",
            color: "text.secondary",
            fontSize: "0.65rem",
            mt: 0.5,
          }}
        >
          Adjusted
        </Typography>
      ) : null}
    </Box>
  ) : (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        color: colors.orange[700],
        fontStyle: editedValues.isServiceLineTotalOverridden ? "italic" : undefined,
      }}
    >
      {formatCurrency(displayLineTotal)}
    </Typography>
  );

  const serviceInformation = (
    <>
      {shouldShowServiceHeader ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: colors.grey[900],
                lineHeight: 1.3,
                overflowWrap: "anywhere",
              }}
            >
              {displayServiceName}
              {visitIndex !== undefined ? ` — Visit ${visitIndex + 1}` : ""}
            </Typography>

            <VerifiedIcon
              sx={{
                fontSize: "0.9rem",
                color: colors.grey[400],
                flexShrink: 0,
              }}
            />
          </Box>

          {serviceAlias ? (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: colors.grey[500],
                fontSize: "0.75rem",
                mt: 0.25,
              }}
            >
              Original service: {originalServiceName}
            </Typography>
          ) : null}

          {displayServiceDescription ? (
            <Box sx={{ mt: 0.75 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: colors.grey[400],
                  fontSize: "0.68rem",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  lineHeight: 1.4,
                }}
              >
                Service description
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: colors.grey[500],
                  fontSize: "0.75rem",
                  lineHeight: 1.4,
                  mt: 0.2,
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {displayServiceDescription}
              </Typography>
            </Box>
          ) : null}
        </>
      ) : null}

      {visit ? (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: colors.grey[600],
            fontSize: "0.75rem",
            mt: shouldShowServiceHeader ? 0.5 : 0,
          }}
        >
          {visit.title}
          {visit.subtitle ? ` · ${visit.subtitle}` : ""}
        </Typography>
      ) : null}

      {shouldShowServiceHeader && squareFootage ? (
        <Box sx={{ mt: 0.75 }}>
          <Chip
            label={`Square Footage: ${squareFootage}`}
            size="small"
            variant="outlined"
            sx={{
              fontSize: "0.65rem",
              height: 20,
              fontWeight: 500,
              borderColor: colors.grey[300],
              color: colors.grey[600],
              bgcolor: colors.grey[50],
            }}
          />
        </Box>
      ) : null}
    </>
  );

  if (isMobile) {
    return (
      <TableRow sx={{ bgcolor: colors.grey[100] }}>
        <TableCell
          colSpan={5}
          sx={{
            px: 2,
            py: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mb: 2,
            }}
          >
            {visitIndex === 0 || !visit ? (
              <IconButton
                size="small"
                onClick={onToggleExpand}
                sx={{
                  color: colors.grey[500],
                  p: 0.5,
                  mt: -0.2,
                }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            ) : (
              <Box sx={{ width: 28, height: 28 }} />
            )}

            <Box sx={{ minWidth: 0, flex: 1 }}>{serviceInformation}</Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {qtyField}
              </Box>

              <Box
                sx={{
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  borderRadius: 2,
                  minHeight: 40,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {unitPriceInput}
              </Box>
            </Box>

            <Box sx={{ minWidth: 120 }}>{lineTotalCell}</Box>
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow
      sx={{
        bgcolor: colors.grey[100],

        "& .MuiTableCell-root": {
          bgcolor: colors.grey[100],
        },
      }}
    >
      <TableCell
        sx={{
          px: 2,
          py: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          {visitIndex === 0 || !visit ? (
            <IconButton
              size="small"
              onClick={onToggleExpand}
              sx={{
                color: colors.grey[500],
                p: 0.5,
                mt: -0.2,
              }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          ) : (
            <Box sx={{ width: 28, height: 28 }} />
          )}

          <Box sx={{ minWidth: 0, flex: 1 }}>{serviceInformation}</Box>
        </Box>
      </TableCell>

      <TableCell
        align="center"
        sx={{
          ...sxCellOrange,
          px: 0,
          py: 2,
        }}
      >
        {qtyField}
      </TableCell>

      <TableCell
        align="right"
        sx={{
          ...sxCellOrange,
          px: 1,
          py: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          {unitPriceInput}
        </Box>
      </TableCell>

      <TableCell
        align="right"
        sx={{
          ...sxCellOrange,
          px: 2,
          py: 2,
        }}
      >
        {lineTotalCell}
      </TableCell>

      <TableCell
        sx={{
          ...sxCellOrange,
          px: 1,
          py: 2,
        }}
      />
    </TableRow>
  );
};
